import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Mail, User, Phone, ShieldCheck /*, GraduationCap */ } from 'lucide-react';
import { LMS } from '@/constants/testIds';
import { BRAND } from '@/data/mockData';
import ParticlesBg from '@/components/common/ParticlesBg';
import { useAuth, formatApiError } from '@/contexts/AuthContext';

// Spec-compliant email pattern: ^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const COUNTRY_CODES = [
  ['+91', 'IND'], ['+1', 'USA'], ['+44', 'GBR'],
  ['+61', 'AUS'], ['+64', 'NZL'], ['+81', 'JPN'], ['+82', 'KOR'],
  ['+86', 'CHN'], ['+65', 'SGP'], ['+60', 'MYS'], ['+62', 'IDN'],
  ['+66', 'THA'], ['+84', 'VNM'], ['+63', 'PHL'], ['+92', 'PAK'],
  ['+880', 'BGD'], ['+94', 'LKA'], ['+977', 'NPL'], ['+971', 'UAE'],
  ['+966', 'SAU'], ['+974', 'QAT'], ['+965', 'KWT'], ['+968', 'OMN'],
  ['+972', 'ISR'], ['+27', 'ZAF'], ['+20', 'EGY'], ['+234', 'NGA'],
  ['+254', 'KEN'], ['+212', 'MAR'], ['+33', 'FRA'], ['+49', 'DEU'],
  ['+39', 'ITA'], ['+34', 'ESP'], ['+31', 'NLD'], ['+41', 'CHE'],
  ['+43', 'AUT'], ['+46', 'SWE'], ['+47', 'NOR'], ['+45', 'DNK'],
  ['+7', 'RUS'], ['+55', 'BRA'], ['+52', 'MEX'], ['+54', 'ARG'],
  ['+56', 'CHL'], ['+57', 'COL'], ['+51', 'PER'],
];

const LearningHub = () => {
  const [tab, setTab] = useState('login');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [regForm, setRegForm] = useState({
    full_name: '', email: '', mobile: '', country_code: '+91', password: '', confirm_password: '',
  });
  const [regErrors, setRegErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { user, login, register } = useAuth();

  // If already authenticated, redirect to dashboard.
  useEffect(() => {
    if (user) navigate(user.role === 'admin' ? '/admin' : '/student', { replace: true });
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error('Please enter both email and password');
      return;
    }
    setSubmitting(true);
    try {
      const u = await login(loginForm.email.trim(), loginForm.password);
      toast.success(`Welcome back, ${u.full_name}`);
      navigate(u.role === 'admin' ? '/admin' : '/student', { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const validateRegister = () => {
    const errors = {};
    const name = (regForm.full_name || '').trim();
    const email = (regForm.email || '').trim().toLowerCase();
    const mobile = (regForm.mobile || '').trim();
    const password = regForm.password || '';
    const confirm = regForm.confirm_password || '';

    if (!name) errors.full_name = 'Full name is required';
    if (!email) errors.email = 'Email Address is required';
    else if (!EMAIL_RE.test(email)) errors.email = 'Please enter a valid email address';

    if (!mobile) errors.mobile = 'Phone Number is required';
    else if (!/^\d+$/.test(mobile)) errors.mobile = 'Only numeric digits are allowed';
    else if (regForm.country_code === '+91' && mobile.length !== 10) errors.mobile = 'Phone Number must contain exactly 10 digits';
    else if (regForm.country_code !== '+91' && (mobile.length < 7 || mobile.length > 15)) errors.mobile = 'Phone Number must contain 7 to 15 digits';

    if (!password) errors.password = 'Password is required';
    else if (password.length < 6) errors.password = 'Password must be at least 6 characters';

    if (!confirm) errors.confirm_password = 'Please confirm your password';
    else if (confirm !== password) errors.confirm_password = 'Passwords do not match';

    return errors;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const errors = validateRegister();
    setRegErrors(errors);
    if (Object.keys(errors).length) {
      toast.error(Object.values(errors)[0]);
      return;
    }
    setSubmitting(true);
    try {
      const u = await register({
        full_name: regForm.full_name.trim(),
        email: regForm.email.trim().toLowerCase(),
        mobile: regForm.mobile.trim(),
        country_code: regForm.country_code || '+91',
        password: regForm.password,
      });
      toast.success(`Welcome to BYMAT, ${u.full_name}`);
      navigate('/student', { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  /*
  // Demo login helpers — kept disabled in the live UI.
  const demoStudent = async () => {
    setLoginForm({ email: 'rohit.demo@gmail.com', password: 'Demo@1234' });
    setSubmitting(true);
    try {
      const u = await login('rohit.demo@gmail.com', 'Demo@1234');
      toast.success(`Welcome, ${u.full_name}`);
      navigate('/student', { replace: true });
    } catch (err) {
      toast.error(formatApiError(err) + ' — make sure the seed account exists.');
    } finally {
      setSubmitting(false);
    }
  };

  const demoAdmin = async () => {
    setLoginForm({ email: 'admin@bymatautomation.com', password: 'Admin@BYMAT2026' });
    setSubmitting(true);
    try {
      const u = await login('admin@bymatautomation.com', 'Admin@BYMAT2026');
      toast.success(`Welcome, ${u.full_name}`);
      navigate('/admin', { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };
  */

  return (
    <section className="relative min-h-[80vh] py-16 lg:py-24 overflow-hidden">
      <div className="absolute inset-0 radial-glow" />
      <ParticlesBg count={30} />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <div className="text-xs tracking-[0.3em] uppercase text-brand-cyan/90 font-mono">Learning Hub Portal</div>
          <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] font-semibold">
            Your <span className="text-brand-cyan">GenAI Automation</span> mentorship vault.
          </h1>
          <p className="mt-5 text-white/70 max-w-md">
            Secure access to your enrolled batch — live session recordings, resources, certificates, and 1:1 mentor support.
          </p>

          <ul className="mt-6 space-y-3 text-sm text-white/75">
            {[
              'Watch session recordings with AI watermark security',
              'Download approved resources (PDFs, code, templates)',
              'Track learning progress with last-watched memory',
              'Claim verified course completion certificates',
              
            ].map((b) => (
              <li key={b} className="flex items-start gap-3">
                <span className="h-5 w-5 rounded-full bg-brand-cyan/15 text-brand-cyan flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">✓</span>
                {b}
              </li>
            ))}
          </ul>

          {/* Demo login buttons disabled in the live portal.
          <div className="mt-8 flex flex-wrap gap-2">
            <button type="button" disabled={submitting} onClick={demoStudent} data-testid={LMS.demoStudentBtn}
              className="text-xs glass px-4 py-2 rounded-lg hover:bg-brand-cyan/10 hover:text-brand-cyan transition inline-flex items-center gap-2 disabled:opacity-50">
              <GraduationCap className="w-3.5 h-3.5" /> Demo as Student
            </button>
            <button type="button" disabled={submitting} onClick={demoAdmin} data-testid={LMS.demoAdminBtn}
              className="text-xs glass px-4 py-2 rounded-lg hover:bg-brand-cyan/10 hover:text-brand-cyan transition inline-flex items-center gap-2 disabled:opacity-50">
              <ShieldCheck className="w-3.5 h-3.5" /> Demo as Admin
            </button>
          </div>
          */}
        </div>

        <div className="lg:col-span-7">
          <div id="sign-in" className="glass-strong rounded-3xl p-6 sm:p-8 relative">
            <div className="flex gap-2 p-1 glass rounded-lg w-fit">
              <button type="button" onClick={() => setTab('login')} data-testid={LMS.loginTab}
                className={`px-5 py-2 text-sm rounded-md font-medium transition ${tab === 'login' ? 'bg-brand-cyan text-black' : 'text-white/70 hover:text-white'}`}>
                Sign In
              </button>
              <button type="button" onClick={() => setTab('register')} data-testid={LMS.registerTab}
                className={`px-5 py-2 text-sm rounded-md font-medium transition ${tab === 'register' ? 'bg-brand-cyan text-black' : 'text-white/70 hover:text-white'}`}>
                Register
              </button>
            </div>

            {tab === 'login' ? (
              <form onSubmit={handleLogin} className="mt-6 space-y-4">
                <h2 className="font-display text-xl font-bold">Welcome back</h2>
                <FieldIcon icon={Mail} type="email" tid={LMS.loginEmail} value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="you@company.com" autoComplete="email" />
                <FieldIcon icon={Lock} type="password" tid={LMS.loginPassword} value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="Your password" autoComplete="current-password" />
                <button type="submit" disabled={submitting} data-testid={LMS.loginSubmit}
                  className="w-full bg-brand-cyan text-black font-semibold py-3 rounded-lg btn-glow hover:bg-brand-cyanSoft transition disabled:opacity-60">
                  {submitting ? 'Signing in...' : 'Sign In'}
                </button>
                <div className="text-center text-xs text-white/50">
                  No account? <button type="button" onClick={() => setTab('register')} className="text-brand-cyan hover:underline">Register here</button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleRegister} className="mt-6 space-y-4" noValidate>
                <h2 className="font-display text-xl font-bold">Create your student account</h2>

                <FieldIcon icon={User} tid={LMS.registerName}
                  value={regForm.full_name}
                  onChange={(e) => setRegForm({ ...regForm, full_name: e.target.value })}
                  placeholder="Full name" autoComplete="name" error={regErrors.full_name} />

                <FieldIcon icon={Mail} type="email" tid={LMS.registerEmail}
                  value={regForm.email}
                  onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                  onBlur={() => {
                    const v = (regForm.email || '').trim().toLowerCase();
                    if (v && !EMAIL_RE.test(v)) setRegErrors((p) => ({ ...p, email: 'Please enter a valid email address' }));
                    else setRegErrors((p) => { const n = { ...p }; delete n.email; return n; });
                  }}
                  placeholder="Email" autoComplete="email" error={regErrors.email} />

                {/* Phone with country code prefix — default India (+91) */}
                <div>
                  <div className="flex items-stretch rounded-lg bg-white/5 border border-white/10 focus-within:border-brand-cyan/60 overflow-hidden">
                    <div className="flex items-center gap-2 px-3 border-r border-white/10 bg-white/5">
                      <select
                        data-testid={LMS.registerCountryCode}
                        value={regForm.country_code}
                        onChange={(e) => setRegForm({ ...regForm, country_code: e.target.value })}
                        aria-label="Country calling code"
                        className="bg-transparent text-sm focus:outline-none px-3 py-3 text-white/80"
                      >
                        {COUNTRY_CODES.map(([code, name]) => (
                          <option key={code} value={code} className="bg-[#050A14]">{code} {name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="relative flex-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                      <input
                        data-testid={LMS.registerPhone}
                        type="tel"
                        inputMode="numeric"
                        pattern="\d{10}"
                        maxLength={15}
                        autoComplete="tel-national"
                        placeholder="9876543210"
                        value={regForm.mobile}
                        onChange={(e) => {
                          // Digits-only + clamp to the international national-number limit.
                          const digits = e.target.value.replace(/\D+/g, '').slice(0, 15);
                          setRegForm({ ...regForm, mobile: digits });
                          if (regErrors.mobile) setRegErrors((p) => { const n = { ...p }; delete n.mobile; return n; });
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const text = (e.clipboardData || window.clipboardData).getData('text');
                          const digits = (text || '').replace(/\D+/g, '').slice(0, 15);
                          setRegForm((p) => ({ ...p, mobile: digits }));
                        }}
                        onKeyDown={(e) => {
                          // Allow control keys + digits only
                          const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
                          if (allowed.includes(e.key) || (e.ctrlKey || e.metaKey)) return;
                          if (!/^\d$/.test(e.key)) e.preventDefault();
                        }}
                        className="w-full pl-9 pr-4 py-3 bg-transparent focus:outline-none placeholder:text-white/30"
                      />
                    </div>
                  </div>
                  {regErrors.mobile && (
                    <p data-testid="register-phone-error" className="mt-1 text-xs text-red-400">{regErrors.mobile}</p>
                  )}
                </div>

                {/* Batch dropdown removed — admins assign students to batches manually. */}

                <FieldIcon icon={Lock} type="password" tid={LMS.registerPassword}
                  value={regForm.password}
                  onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                  placeholder="Create password (min 6 chars)" autoComplete="new-password" error={regErrors.password} />

                <FieldIcon icon={ShieldCheck} type="password" tid={LMS.registerConfirmPassword}
                  value={regForm.confirm_password}
                  onChange={(e) => setRegForm({ ...regForm, confirm_password: e.target.value })}
                  placeholder="Confirm password" autoComplete="new-password" error={regErrors.confirm_password} />

                <button type="submit" disabled={submitting} data-testid={LMS.registerSubmit}
                  className="w-full bg-brand-cyan text-black font-semibold py-3 rounded-lg btn-glow hover:bg-brand-cyanSoft transition disabled:opacity-60">
                  {submitting ? 'Creating account...' : 'Create Account'}
                </button>
                <div className="text-center text-xs text-white/50">
                  Already have an account? <button type="button" onClick={() => setTab('login')} className="text-brand-cyan hover:underline">Sign in</button>
                </div>
              </form>
            )}
          </div>

          <p className="mt-4 text-[11px] text-white/40 text-center">
            Secured with JWT · single-active-session per account.{' '}
            <Link to="/" className="text-brand-cyan/80 hover:text-brand-cyan">Back to {BRAND.name}</Link>
          </p>
        </div>
      </div>
    </section>
  );
};

const FieldIcon = ({ icon: Icon, type = 'text', tid, value, onChange, onBlur, placeholder, autoComplete, error }) => (
  <div>
    <div className="relative">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
      <input
        type={type}
        data-testid={tid}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className={`w-full pl-11 pr-4 py-3 rounded-lg bg-white/5 border focus:outline-none placeholder:text-white/30 ${error ? 'border-red-400/60' : 'border-white/10 focus:border-brand-cyan/60'}`}
      />
    </div>
    {error && <p data-testid={`${tid}-error`} className="mt-1 text-xs text-red-400">{error}</p>}
  </div>
);

export default LearningHub;
